import { useState, useEffect } from 'react';
import { useOutlets } from '@/hooks/useOutlets';
import { useUsers } from '@/hooks/useUsers';
import { useFrames } from '@/hooks/useFrames';
import { useProducts } from '@/hooks/useProducts';
import { api } from '@/lib/api';
import { startOfDay, endOfDay } from 'date-fns';
import type { OutletComparison } from '@photobooth/types';

export function Dashboard() {
  const { outlets, isLoading: isLoadingOutlets } = useOutlets();
  const { users, isLoading: isLoadingUsers } = useUsers();
  const { frames, isLoading: isLoadingFrames } = useFrames();
  const { products, isLoading: isLoadingProducts } = useProducts();
  const [todayRevenue, setTodayRevenue] = useState<OutletComparison[]>([]);
  const [isLoadingTodayRevenue, setIsLoadingTodayRevenue] = useState(false);

  const activeUsersCount = users.filter((u) => u.isActive).length;

  useEffect(() => {
    const fetchTodayRevenue = async () => {
      setIsLoadingTodayRevenue(true);
      try {
        const response = await api.reports.getOutletComparison(
          startOfDay(new Date()),
          endOfDay(new Date())
        );
        if (response.success && response.data) {
          const data = (response.data as any).data ?? response.data;
          setTodayRevenue(Array.isArray(data) ? data : []);
        }
      } catch {
        setTodayRevenue([]);
      } finally {
        setIsLoadingTodayRevenue(false);
      }
    };
    fetchTodayRevenue();
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-bold text-text mb-6">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-surface p-6 rounded-lg shadow">
          <h3 className="text-text-muted text-sm font-medium">Total Outlets</h3>
          <p className="text-3xl font-bold text-text mt-2">
            {isLoadingOutlets ? '-' : outlets.length}
          </p>
        </div>
        <div className="bg-surface p-6 rounded-lg shadow">
          <h3 className="text-text-muted text-sm font-medium">Active Users</h3>
          <p className="text-3xl font-bold text-text mt-2">
            {isLoadingUsers ? '-' : activeUsersCount}
          </p>
        </div>
        <div className="bg-surface p-6 rounded-lg shadow">
          <h3 className="text-text-muted text-sm font-medium">Total Frames</h3>
          <p className="text-3xl font-bold text-text mt-2">
            {isLoadingFrames ? '-' : frames.length}
          </p>
        </div>
        <div className="bg-surface p-6 rounded-lg shadow">
          <h3 className="text-text-muted text-sm font-medium">Total Products</h3>
          <p className="text-3xl font-bold text-text mt-2">
            {isLoadingProducts ? '-' : products.length}
          </p>
        </div>
      </div>

      <div className="mt-8 bg-surface p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold text-text mb-4">Outlets Revenue Today</h2>
        {isLoadingTodayRevenue ? (
          <p className="text-text-muted">Loading...</p>
        ) : outlets.length === 0 ? (
          <p className="text-text-muted italic">No outlets</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Outlet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Transactions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const revenueMap = new Map(
                    todayRevenue.map((r) => [r.outletId, { totalAmount: r.totalAmount, transactionCount: r.transactionCount }])
                  );
                  const merged = outlets.map((outlet) => ({
                    outlet,
                    totalAmount: revenueMap.get(outlet.outletId)?.totalAmount ?? 0,
                    transactionCount: revenueMap.get(outlet.outletId)?.transactionCount ?? 0,
                  }));
                  const sorted = [...merged].sort((a, b) => b.totalAmount - a.totalAmount);
                  return sorted.map(({ outlet, totalAmount, transactionCount }) => (
                    <tr key={outlet.outletId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">{outlet.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                        Rp {(totalAmount ?? 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">{transactionCount}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-8 bg-surface p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold text-text mb-4">Welcome to Admin Dashboard</h2>
        <p className="text-text-muted">
          Use the sidebar to navigate to different sections of the admin panel.
        </p>
      </div>
    </div>
  );
}
